import type { UserUuid } from "../identity";
// src/types/finance/CatalogueTypes.ts
//
// ── What the invoice line-item picker receives (S11 consumes S7) ──────────────────────────────
//
// The frontend mirror of the finance service's catalogue DTOs
// (`finance/src/modules/catalogue/dto/catalogue-response.dto.ts`). That DTO is a hand-built
// PROJECTION of commerce and healthcare, not a pass-through, and this copy must stay a faithful
// mirror of it — a field appears here because it appears there.
//
// ⚠️ **MONEY IS INTEGER CENTS AND `null` STAYS `null`.** `unitPriceCents` has already been
// converted once, inside finance's client services (the ONE money boundary, S6). Nothing on this
// side divides, multiplies or re-parses it, and in particular `utils/finance/money.ts`'s
// `dollarsToCents` must never be pointed at it — that function RETURNS 0 for input it cannot
// parse, and a `$0.00` line reads to a patient as "nothing owed". A `null` is a genuinely unpriced
// item (the common case for an appointment type) and travels all the way to the picker as `null`,
// which no-ops the prefill. Render it with `formatCents`, which already produces an em dash.
//
// ⚠️ `available` is a stock COUNT, not money. It never goes near a money helper.
//
// ── Deliberately absent, mirroring the server ────────────────────────────────────────────────
//
// `status` / `isActive` (both upstreams already filter to active-only), `description` on an
// appointment type, `defaultPriceCents` / `customPriceCents` (the picker prefills ONE number), and
// `isDefaultVariant` — parent decision D6 is that the picker asks for product AND variant, both
// explicit. **Do not synthesise a default variant client-side**; if preselection is wanted the
// field has to be added to the server projection first.

/** One purchasable variant of a product. Stock is keyed here, not on the product. */
export interface CatalogueVariant {
	variantId: number;
	title: string;
	sku: string;
	/** Integer cents, or `null` when commerce sent an unparseable price. Never coerce to 0. */
	unitPriceCents: number | null;
	/** Sum of the variant's available stock rows. An INTEGER COUNT, not money. May be negative. */
	available: number;
}

export interface CatalogueProduct {
	productId: number;
	name: string;
	sku: string;
	variants: CatalogueVariant[];
}

export interface CataloguePagination {
	page: number;
	perPage: number;
	totalItems: number;
	totalPages: number;
}

export interface CatalogueAppointmentType {
	appointmentTypeId: number;
	name: string;
	durationMinutes: number;
	/** The EFFECTIVE price — `custom_price ?? default_price` (D7). `null` ⇒ the type is unpriced. */
	unitPriceCents: number | null;
}

/**
 * The degrade marker, and the reason the two empty responses are not the same thing.
 *
 * Present and `true` ONLY when finance's upstream call FAILED — the picker should say the
 * catalogue is temporarily unavailable and let the practitioner type the line by hand.
 *
 * ⚠️ It is deliberately ABSENT when the clinic simply has no catalogue wired up (finance logs
 * `catalogue.*.not_configured`) or when the catalogue is genuinely empty. Both of those are 200s
 * with an empty list and NO flag, because they are permanent states a retry cannot fix. Either way
 * losing the picker must never cost the invoice. `catalogueEmptyState()` in
 * `components/finance/invoicing/catalogueSearch.ts` is the one place that classifies the three.
 */
export interface CatalogueDegradable {
	unavailable?: true;
}

export interface CatalogueProductsResponse extends CatalogueDegradable {
	data: CatalogueProduct[];
	pagination: CataloguePagination;
}

export interface CatalogueAppointmentTypesResponse extends CatalogueDegradable {
	data: CatalogueAppointmentType[];
}

/**
 * Query parameters, in the frontend's camelCase. `CatalogueApi` renames `perPage` to `per_page` on
 * the way out — see its docblock, this is not cosmetic.
 */
export interface CatalogueProductsParams {
	search?: string;
	page?: number;
	perPage?: number;
}

export interface CatalogueAppointmentTypesParams {
	practitionerId?: UserUuid;
}

/**
 * ⚠️ `per_page` above this is a **400 naming the field**, not a clamp — finance rejects it
 * deliberately (commerce answers 422 above 100, and a clamp would silently serve 100 rows to a
 * caller that believed it had the whole list).
 */
export const CATALOGUE_MAX_PER_PAGE = 100;

/** Longest free-text search finance will accept (`@MaxLength(255)`, matching commerce). */
export const CATALOGUE_MAX_SEARCH_LENGTH = 255;

/** The pagination block a degraded or malformed response is normalised to. */
export const EMPTY_CATALOGUE_PAGINATION: CataloguePagination = {
	page: 1,
	perPage: 0,
	totalItems: 0,
	totalPages: 0,
};
